import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Award,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  CircleDollarSign,
  Crown,
  Image,
  Layers,
  Megaphone,
  MessageSquareText,
  PenTool,
  Sparkles,
  Star,
  TrendingUp,
  User,
  Users,
  Workflow,
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { publicApi } from '../utils/api';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const scheduleIdleWork = (callback, timeout = 250) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const id = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(id);
  }
  const id = window.setTimeout(() => callback(), 0);
  return () => window.clearTimeout(id);
};

const SORT_OPTIONS = [
  {
    id: 'overall',
    label: 'Best Tools',
    shortLabel: 'Best',
    icon: Crown,
    accent: 'var(--glow-1)',
    border: 'rgba(var(--glow-1-rgb), 0.28)',
    background: 'linear-gradient(135deg, rgba(var(--glow-1-rgb), 0.16), rgba(var(--glow-1-rgb), 0.03))',
    description: 'Highest rated with the strongest overall fit.',
  },
  {
    id: 'budget',
    label: 'Cheapest',
    shortLabel: 'Budget',
    icon: CircleDollarSign,
    accent: 'var(--glow-2)',
    border: 'rgba(var(--glow-2-rgb), 0.26)',
    background: 'linear-gradient(135deg, rgba(var(--glow-2-rgb), 0.14), rgba(var(--glow-2-rgb), 0.03))',
    description: 'Best low-cost or free starting points.',
  },
  {
    id: 'trending',
    label: 'Trending',
    shortLabel: 'Trending',
    icon: TrendingUp,
    accent: 'var(--glow-4)',
    border: 'rgba(var(--glow-4-rgb), 0.28)',
    background: 'linear-gradient(135deg, rgba(var(--glow-4-rgb), 0.16), rgba(var(--glow-4-rgb), 0.03))',
    description: 'Most actively evaluated by buyers right now.',
  },
  {
    id: 'solo',
    label: 'Best for Solo',
    shortLabel: 'Solo',
    icon: User,
    accent: 'var(--glow-3)',
    border: 'rgba(var(--glow-3-rgb), 0.26)',
    background: 'linear-gradient(135deg, rgba(var(--glow-3-rgb), 0.15), rgba(var(--glow-3-rgb), 0.03))',
    description: 'Lean picks for individuals and very small teams.',
  },
  {
    id: 'teams',
    label: 'Best for Teams',
    shortLabel: 'Teams',
    icon: Users,
    accent: 'var(--glow-5)',
    border: 'rgba(var(--glow-5-rgb), 0.26)',
    background: 'linear-gradient(135deg, rgba(var(--glow-5-rgb), 0.16), rgba(var(--glow-5-rgb), 0.03))',
    description: 'Picks that favor collaboration, admin control, and scale.',
  },
];

const ACCENT_PALETTE = [
  { accent: 'var(--glow-1)', bg: 'linear-gradient(135deg, rgba(var(--glow-1-rgb), 0.18), rgba(var(--glow-1-rgb), 0.02))' },
  { accent: 'var(--glow-2)', bg: 'linear-gradient(135deg, rgba(var(--glow-2-rgb), 0.18), rgba(var(--glow-2-rgb), 0.02))' },
  { accent: 'var(--glow-3)', bg: 'linear-gradient(135deg, rgba(var(--glow-3-rgb), 0.18), rgba(var(--glow-3-rgb), 0.02))' },
  { accent: 'var(--glow-4)', bg: 'linear-gradient(135deg, rgba(var(--glow-4-rgb), 0.18), rgba(var(--glow-4-rgb), 0.02))' },
  { accent: 'var(--glow-5)', bg: 'linear-gradient(135deg, rgba(var(--glow-5-rgb), 0.18), rgba(var(--glow-5-rgb), 0.02))' },
  { accent: 'var(--glow-6)', bg: 'linear-gradient(135deg, rgba(var(--glow-6-rgb), 0.18), rgba(var(--glow-6-rgb), 0.02))' },
];

const slugify = (value = '') =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const toCurrencyValue = (value = '') => {
  const normalized = String(value || '').toLowerCase().trim();
  if (!normalized) {
    return Number.POSITIVE_INFINITY;
  }
  if (normalized.includes('free')) {
    return 0;
  }
  if (normalized.includes('custom') || normalized.includes('contact')) {
    return 100000;
  }
  const match = normalized.match(/(\d+[\d,.]*)/);
  if (!match) {
    return 50000;
  }
  return Number(match[1].replace(/,/g, '')) || 50000;
};

const toFiniteNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const getMonthlyPriceEstimate = (tool = {}) => {
  const explicit = toFiniteNumber(tool.monthly_price_estimate, Number.NaN);
  if (Number.isFinite(explicit) && explicit >= 0) {
    return explicit;
  }
  const inferred = toCurrencyValue(tool.price_label);
  if (!Number.isFinite(inferred) || inferred >= 50000) {
    return 250;
  }
  return Math.max(0, inferred);
};

const getRatingEstimate = (tool = {}) => {
  const direct = toFiniteNumber(tool?.rating?.overall, Number.NaN);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const fromReviews = toFiniteNumber(tool?.review_snapshot?.average_rating, Number.NaN);
  if (Number.isFinite(fromReviews) && fromReviews > 0) {
    return fromReviews;
  }
  const qualityComponent = toFiniteNumber(tool?.ranking_snapshot?.components?.quality, Number.NaN);
  if (Number.isFinite(qualityComponent) && qualityComponent > 0) {
    return qualityComponent / 20;
  }
  return 0;
};

const getRankingScore = (tool = {}) => {
  const direct = toFiniteNumber(tool?.ranking_score, Number.NaN);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const fromSnapshot = toFiniteNumber(tool?.ranking_snapshot?.overall_score, Number.NaN);
  if (Number.isFinite(fromSnapshot) && fromSnapshot > 0) {
    return fromSnapshot;
  }
  return 0;
};

const getAdoptionSignal = (tool = {}) => {
  const direct = toFiniteNumber(tool?.competitor_adoption_pct, Number.NaN);
  if (Number.isFinite(direct) && direct > 0) {
    return direct;
  }
  const fromPriming = toFiniteNumber(tool?.priming_signals?.competitor_adoption_pct, Number.NaN);
  if (Number.isFinite(fromPriming) && fromPriming > 0) {
    return fromPriming;
  }
  return 0;
};

const getComplexityScore = (tool = {}) => {
  const direct = toFiniteNumber(tool?.implementation_complexity_score, Number.NaN);
  if (Number.isFinite(direct) && direct >= 0) {
    return direct;
  }
  const fromPriming = toFiniteNumber(tool?.priming_signals?.complexity_score, Number.NaN);
  if (Number.isFinite(fromPriming) && fromPriming >= 0) {
    return fromPriming;
  }
  return 50;
};

const getIconForGroup = (label = '') => {
  const normalized = label.toLowerCase();
  if (normalized.includes('writing') || normalized.includes('content') || normalized.includes('copy')) {
    return PenTool;
  }
  if (normalized.includes('image') || normalized.includes('design') || normalized.includes('video')) {
    return Image;
  }
  if (normalized.includes('seo') || normalized.includes('marketing') || normalized.includes('social')) {
    return Megaphone;
  }
  if (normalized.includes('support') || normalized.includes('chat') || normalized.includes('customer')) {
    return MessageSquareText;
  }
  if (normalized.includes('automation') || normalized.includes('agent') || normalized.includes('workflow')) {
    return Workflow;
  }
  if (normalized.includes('analytics') || normalized.includes('data') || normalized.includes('intelligence')) {
    return BarChart3;
  }
  if (normalized.includes('hr') || normalized.includes('team') || normalized.includes('enterprise')) {
    return Briefcase;
  }
  if (normalized.includes('code') || normalized.includes('developer')) {
    return Bot;
  }
  return Layers;
};

const resolveCriterion = (pathname, search) => {
  const params = new URLSearchParams(search);
  const requested = params.get('sort') || params.get('criterion');
  if (requested && SORT_OPTIONS.some((option) => option.id === requested)) {
    return requested;
  }
  const legacyTab = params.get('tab');
  if (legacyTab === 'lists') {
    return 'trending';
  }
  if (legacyTab === 'picks' || pathname.startsWith('/top-picks')) {
    return 'overall';
  }
  return 'overall';
};

const normalizeAudienceLabel = (value = '') => {
  const normalized = String(value || '').replace(/-/g, ' ').trim();
  if (!normalized) {
    return 'Guide';
  }
  return normalized.replace(/\b\w/g, (char) => char.toUpperCase());
};

const pickPrimaryPage = (pages = []) =>
  [...pages].sort((left, right) => (right.table_rows || []).length - (left.table_rows || []).length)[0] || null;

const getToolUniqueKey = (tool = {}) => {
  const slug = String(tool.slug || '').trim().toLowerCase();
  if (slug) return `slug:${slug}`;
  const id = String(tool.id || tool.tool_id || '').trim().toLowerCase();
  if (id) return `id:${id}`;
  const name = String(tool.name || '').trim().toLowerCase();
  return name ? `name:${name}` : '';
};

const mergeToolData = (row = {}) => {
  const pricingTier = Array.isArray(row.pricing_tiers) ? row.pricing_tiers[0] || {} : {};
  return {
    ...row,
    slug: row.slug,
    id: row.tool_id || row.id,
    name: row.name,
    rating: row.rating,
    tagline: row.tagline || row.best_for,
    best_for: row.best_for || '',
    price_label: row.price || pricingTier.price || row.price_label || 'Pricing varies',
    clicks: row.clicks || 0,
    views: row.views || 0,
    category_name: row.category_name || row.category_label || '',
    description: row.description || row.summary || '',
  };
};

const keywordScore = (tool, positive = [], negative = []) => {
  const haystack = [tool.name, tool.tagline, tool.best_for, tool.description, tool.category_name]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  let score = 0;
  positive.forEach((token) => {
    if (haystack.includes(token)) {
      score += 1;
    }
  });
  negative.forEach((token) => {
    if (haystack.includes(token)) {
      score -= 1;
    }
  });
  return score;
};

const rankTools = (tools = [], criterion = 'overall', trendingLookup = new Map()) => {
  const deduped = [];
  const seen = new Set();
  (tools || []).forEach((tool) => {
    if (!tool || !tool.name) return;
    const key = getToolUniqueKey(tool);
    if (!key || seen.has(key)) return;
    seen.add(key);
    deduped.push(tool);
  });

  const prepared = deduped.map((tool) => {
    const rating = getRatingEstimate(tool);
    const clicks = Number(trendingLookup.get(tool.slug) || tool.clicks || 0);
    const priceValue = getMonthlyPriceEstimate(tool);
    const rankingScore = getRankingScore(tool);
    const adoption = getAdoptionSignal(tool);
    const complexity = getComplexityScore(tool);
    const roiNet = toFiniteNumber(tool?.roi_snapshot?.monthly_net, 0);
    return { tool, rating, clicks, priceValue, rankingScore, adoption, complexity, roiNet };
  });

  if (criterion === 'budget') {
    return prepared.sort((left, right) => {
      const priceDelta = left.priceValue - right.priceValue;
      if (priceDelta !== 0) {
        return priceDelta;
      }
      const ratingDelta = right.rating - left.rating;
      if (ratingDelta !== 0) {
        return ratingDelta;
      }
      return left.complexity - right.complexity;
    }).map((entry) => entry.tool);
  }
  const scoreFor = (entry) => {
    const { tool, rating, clicks, priceValue, rankingScore, adoption, complexity, roiNet } = entry;
    if (criterion === 'trending') {
      return clicks * 8 + adoption * 1.15 + rating * 14 + rankingScore * 0.9 + Math.max(roiNet, 0) * 0.04;
    }
    if (criterion === 'solo') {
      return (
        rating * 22 +
        clicks * 2.2 +
        (100 - Math.min(priceValue, 300)) * 0.55 +
        (100 - complexity) * 0.45 +
        keywordScore(tool, ['solo', 'creator', 'freelancer', 'personal', 'simple', 'lightweight', 'startup', 'small team'], ['enterprise', 'security', 'admin']) * 12 -
        Math.min(priceValue, 220) * 0.9 +
        Math.max(roiNet, 0) * 0.03
      );
    }
    if (criterion === 'teams') {
      return (
        rating * 24 +
        clicks * 2.4 +
        rankingScore * 1.05 +
        adoption * 0.65 +
        keywordScore(tool, ['team', 'teams', 'workflow', 'admin', 'enterprise', 'collaboration', 'security', 'shared'], ['freelancer', 'solo']) * 14 -
        Math.min(priceValue, 400) * 0.16 +
        Math.max(roiNet, 0) * 0.035
      );
    }
    return rating * 24 + rankingScore * 1.1 + clicks * 1.9 + adoption * 0.5 + Math.max(roiNet, 0) * 0.03 - Math.min(priceValue, 300) * 0.12;
  };
  return prepared.sort((left, right) => {
    const delta = scoreFor(right) - scoreFor(left);
    if (delta !== 0) {
      return delta;
    }
    return String(left.tool.name || '').localeCompare(String(right.tool.name || ''));
  }).map((entry) => entry.tool);
};

const buildUseCaseGroups = (bestPages = [], categories = []) => {
  const categoryByName = new Map(categories.map((category) => [String(category.name || '').toLowerCase(), category]));
  const categoryBySlug = new Map(categories.map((category) => [String(category.slug || '').toLowerCase(), category]));

  const groups = new Map();
  categories.forEach((category, index) => {
    const id = category.slug || slugify(category.name || category.slug);
    groups.set(id, {
      id,
      label: category.name,
      icon: getIconForGroup(category.name),
      accent: ACCENT_PALETTE[index % ACCENT_PALETTE.length],
      description: category.description || `Browse ranked tools for ${category.name}.`,
      categorySlug: category.slug,
      pages: [],
      useCases: new Set(),
      tools: new Map(),
    });
  });

  bestPages.forEach((page) => {
    const matchedCategory = categoryByName.get(String(page.category_label || '').toLowerCase())
      || categoryBySlug.get(String(page.category_slug || '').toLowerCase());
    const label = matchedCategory?.name || page.category_label || page.use_case || 'AI Tools';
    const id = matchedCategory?.slug || slugify(matchedCategory?.name || label);
    if (!groups.has(id)) {
      groups.set(id, {
        id,
        label,
        icon: getIconForGroup(label),
        accent: ACCENT_PALETTE[groups.size % ACCENT_PALETTE.length],
        description: page.intro || page.meta_description || `Ranked tools for ${label}.`,
        categorySlug: matchedCategory?.slug || null,
        pages: [],
        useCases: new Set(),
        tools: new Map(),
      });
    }
    const group = groups.get(id);
    group.pages.push(page);
    if (page.use_case) {
      group.useCases.add(page.use_case);
    }
    (page.table_rows || []).forEach((row) => {
      const key = getToolUniqueKey(row);
      if (key && !group.tools.has(key)) {
        group.tools.set(key, mergeToolData(row));
      }
    });
  });

  return Array.from(groups.values())
    .map((group) => ({
      ...group,
      useCases: Array.from(group.useCases).slice(0, 4),
      tools: Array.from(group.tools.values()),
      primaryPage: pickPrimaryPage(group.pages),
      toolCount: group.tools.size,
    }))
    .filter((group) => group.toolCount > 0)
    .sort((left, right) => {
      const leftIndex = categories.findIndex((category) => String(category.name || '').toLowerCase() === String(left.label || '').toLowerCase());
      const rightIndex = categories.findIndex((category) => String(category.name || '').toLowerCase() === String(right.label || '').toLowerCase());
      if (leftIndex >= 0 && rightIndex >= 0) {
        return leftIndex - rightIndex;
      }
      if (leftIndex >= 0) {
        return -1;
      }
      if (rightIndex >= 0) {
        return 1;
      }
      return right.toolCount - left.toolCount;
    });
};

const BestPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const location = useLocation();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [topTools, setTopTools] = useState([]);
  const [topPicks, setTopPicks] = useState([]);
  const [bestPages, setBestPages] = useState([]);
  const [spotlightTool, setSpotlightTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matrixRows, setMatrixRows] = useState([]);
  const [activeCriterion, setActiveCriterion] = useState(resolveCriterion(location.pathname, location.search));
  const [activeGroupId, setActiveGroupId] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setActiveCriterion(resolveCriterion(location.pathname, location.search));
  }, [location.pathname, location.search]);

  const loadData = async () => {
    // Phase 1: fetch the minimum data needed to build rankings immediately.
    try {
      const [catRes, topRes, bestRes, settingsRes] = await Promise.allSettled([
        publicApi.getCategories(),
        publicApi.getTopToolsByClicks(18),
        publicApi.getBestPages(240),
        publicApi.getSiteSettings(),
      ]);
      const categoriesData = catRes.status === 'fulfilled' ? (catRes.value?.data || []) : [];
      const topToolsData = topRes.status === 'fulfilled' ? (topRes.value?.data || []) : [];
      const bestPagesData = bestRes.status === 'fulfilled' ? (bestRes.value?.data?.items || []) : [];
      const spotlightId = settingsRes.status === 'fulfilled' && settingsRes.value?.data?.spotlight_enabled
        ? settingsRes.value?.data?.spotlight_tool_id
        : null;

      setCategories(categoriesData);
      setTopTools(topToolsData);
      setBestPages(bestPagesData);

      setTopPicks(
        [...topToolsData]
          .filter((tool) => tool?.rating?.overall || tool?.clicks)
          .sort((left, right) => (right.rating?.overall || 0) - (left.rating?.overall || 0) || (right.clicks || 0) - (left.clicks || 0))
          .slice(0, 18)
      );

      if (spotlightId) {
        const fromTopTools = topToolsData.find((tool) => (tool.id || tool.slug) === spotlightId);
        if (fromTopTools) {
          setSpotlightTool(fromTopTools);
        } else {
          const spotlightFromPages = bestPagesData
            .flatMap((page) => page.table_rows || [])
            .find((tool) => (tool.tool_id || tool.id || tool.slug) === spotlightId);
          setSpotlightTool(spotlightFromPages ? mergeToolData(spotlightFromPages) : null);
        }
      } else {
        setSpotlightTool(null);
      }
    } catch (err) {
      console.error('Rankings initial load failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const useCaseGroups = useMemo(() => buildUseCaseGroups(bestPages, categories), [bestPages, categories]);

  useEffect(() => {
    if (!useCaseGroups.length) {
      return;
    }
    const requestedGroup = new URLSearchParams(location.search).get('usecase');
    const nextGroupId = useCaseGroups.some((group) => group.id === requestedGroup) ? requestedGroup : useCaseGroups[0].id;
    setActiveGroupId((current) => (useCaseGroups.some((group) => group.id === current) ? current : nextGroupId));
  }, [useCaseGroups, location.search]);

  const trendingLookup = useMemo(() => {
    const map = new Map();
    topTools.forEach((tool, index) => {
      map.set(tool.slug, Number(tool.clicks || Math.max(12 - index, 1)));
    });
    return map;
  }, [topTools]);

  const selectedGroup = useMemo(
    () => useCaseGroups.find((group) => group.id === activeGroupId) || useCaseGroups[0] || null,
    [activeGroupId, useCaseGroups]
  );

  const selectedSortOption = useMemo(
    () => SORT_OPTIONS.find((option) => option.id === activeCriterion) || SORT_OPTIONS[0],
    [activeCriterion]
  );

  const selectedShortlist = useMemo(() => {
    if (!selectedGroup) {
      return [];
    }
    return rankTools(selectedGroup.tools, activeCriterion, trendingLookup).slice(0, 6);
  }, [selectedGroup, activeCriterion, trendingLookup]);

  // Lazy matrix: compute after paint and chunk work so rankings become interactive immediately.
  useEffect(() => {
    if (!useCaseGroups.length) return;
    setMatrixRows([]);

    let cancelled = false;
    const chunkSize = 4;

    const buildChunk = (startIndex = 0) => {
      if (cancelled) return;
      const nextRows = useCaseGroups.slice(startIndex, startIndex + chunkSize).map((group) => ({
        ...group,
        cells: SORT_OPTIONS.map((option) => ({
          option,
          tools: rankTools(group.tools, option.id, trendingLookup).slice(0, 2),
        })),
      }));

      setMatrixRows((current) => {
        const prefix = startIndex === 0 ? [] : current.slice(0, startIndex);
        return [...prefix, ...nextRows];
      });

      if (startIndex + chunkSize < useCaseGroups.length) {
        scheduleIdleWork(() => buildChunk(startIndex + chunkSize), 120);
      }
    };

    const cancel = scheduleIdleWork(() => buildChunk(0), 120);
    return () => {
      cancelled = true;
      cancel();
    };
  }, [useCaseGroups, trendingLookup]);

  const handleStateChange = (nextCriterion, nextGroupId) => {
    const criterion = nextCriterion || activeCriterion;
    const groupId = nextGroupId || activeGroupId || useCaseGroups[0]?.id;
    setActiveCriterion(criterion);
    if (groupId) {
      setActiveGroupId(groupId);
    }
    const params = new URLSearchParams();
    params.set('sort', criterion);
    if (groupId) {
      params.set('usecase', groupId);
    }
    navigate(`/best?${params.toString()}`);
  };

  const featuredSignals = useMemo(
    () => [
      {
        label: 'Live Click Leaders',
        value: topTools.length ? `${topTools.length}` : '0',
        helper: 'Trending lists and editor picks now live together.',
      },
      {
        label: 'Use Cases In Matrix',
        value: `${useCaseGroups.length}`,
        helper: 'Browse categories from the left rail instead of opening one page at a time.',
      },
      {
        label: 'One-Click Shortlists',
        value: `${SORT_OPTIONS.length}`,
        helper: 'Best, budget, trending, solo, and team filters share the same view.',
      },
    ],
    [topTools.length, useCaseGroups.length]
  );

  const quickCategoryPicks = useMemo(() =>
    useCaseGroups
      .map((group) => ({
        ...group,
        topThree: rankTools(group.tools, 'overall', trendingLookup).slice(0, 3),
      }))
      .filter((group) => group.topThree.length > 0)
      .slice(0, 10),
  [useCaseGroups, trendingLookup]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyber-yellow" />
      </div>
    );
  }

  const SelectedGroupIcon = selectedGroup?.icon || Layers;

  return (
    <div className="min-h-screen pt-24" data-testid="best-page" style={{ background: 'var(--bg-base)' }}>
      <Helmet>
        <title>Best AI Tool Rankings by Use Case | Wowrolla</title>
        <meta
          name="description"
          content="Browse AI rankings in a single matrix: use cases on the left, sorting criteria on top, and direct shortlist previews without opening dozens of pages."
        />
        <link rel="canonical" href="https://wowrolla.ca/best" />
      </Helmet>

      <section
        className="relative overflow-hidden border-b"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.70), rgba(247,252,255,0.84)), url(${IMG.hero.trophyAward})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.95)), url(${IMG.hero.trophyAward})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          borderColor: isLight ? 'rgba(15, 23, 42, 0.12)' : 'rgba(255,255,255,0.1)',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-8 lg:py-10 relative z-10">
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 rounded-full border"
              style={{
                borderColor: isLight ? 'rgba(154, 107, 0, 0.34)' : 'rgba(255,255,255,0.15)',
                background: isLight ? 'rgba(255,255,255,0.70)' : 'rgba(0,0,0,0.35)',
              }}
            >
              <Sparkles size={14} className="text-cyber-yellow" />
              <span className="text-xs text-cyber-yellow uppercase tracking-widest font-mono">Rankings Console</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-5 items-end">
              <div className="max-w-4xl">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: isLight ? 'var(--text-primary)' : '#FFFFFF' }}>
                  AI rankings that are actually scannable.
                </h1>
                <p className="text-sm sm:text-base max-w-3xl" style={{ color: isLight ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
                  Cleaner navigation, direct shortlist previews, and category-aware guides that update when your tool inventory changes.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {featuredSignals.map((signal, index) => (
                  <div
                    key={signal.label}
                    className="rounded-2xl p-4 backdrop-blur-sm"
                    style={{
                      border: `1px solid ${ACCENT_PALETTE[index % ACCENT_PALETTE.length].accent}33`,
                      background: ACCENT_PALETTE[index % ACCENT_PALETTE.length].bg,
                    }}
                  >
                    <div className="text-[10px] font-mono uppercase tracking-[0.18em] mb-2 leading-tight" style={{ color: ACCENT_PALETTE[index % ACCENT_PALETTE.length].accent }}>
                      {signal.label}
                    </div>
                    <div className="text-2xl font-black mb-1" style={{ color: isLight ? 'var(--text-primary)' : '#FFFFFF' }}>{signal.value}</div>
                    <p className="text-xs leading-5" style={{ color: isLight ? 'var(--text-secondary)' : 'var(--text-secondary)' }}>{signal.helper}</p>
                  </div>
                ))}
              </div>
            </div>


          </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-5">
        <div className="terminal-grid p-4 flex flex-wrap items-center justify-between gap-3" style={{ borderColor: 'rgba(var(--glow-1-rgb), 0.22)' }}>
          <div className="flex items-center gap-3">
            <span className="px-2 py-1 text-[11px] font-mono uppercase tracking-[0.16em]" style={{ color: 'var(--glow-1)', border: '1px solid rgba(var(--glow-1-rgb), 0.4)', background: 'rgba(var(--glow-1-rgb), 0.08)' }}>
              Sponsored Tool Slot
            </span>
            {spotlightTool ? (
              <span className="text-sm text-white">
                {spotlightTool.name}
              </span>
            ) : (
              <span className="text-sm text-text-secondary">Reserved for paid featured placement.</span>
            )}
          </div>
          {spotlightTool ? (
            <Link
              to={spotlightTool.slug ? `/tools/${spotlightTool.slug}` : '/best'}
              className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.14em]"
              style={{ color: 'var(--glow-1)' }}
            >
              View sponsored tool <ArrowRight size={13} />
            </Link>
          ) : (
            <span className="text-xs text-text-muted uppercase tracking-[0.14em]">Available</span>
          )}
        </div>
      </section>

      <section className="container mx-auto px-6 pb-2">
        <div className="terminal-grid p-5 lg:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.18em]" style={{ color: 'var(--glow-3)' }}>Quick Format</p>
              <h2 className="text-2xl font-black" style={{ color: 'var(--text-primary)', fontFamily: 'Unbounded, sans-serif' }}>
                Top tools by category
              </h2>
              <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                Fast shortlist view for buyers: each card shows the top 3 tools in a category without opening separate pages.
              </p>
            </div>
            <span className="text-xs font-mono uppercase tracking-[0.16em]" style={{ color: 'var(--text-muted)' }}>
              Updates with live ranking data
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {quickCategoryPicks.map((group) => {
              const GroupIcon = group.icon;
              return (
                <div
                  key={`quick-${group.id}`}
                  className="rounded-2xl p-4"
                  style={{
                    border: `1px solid ${group.accent.accent}33`,
                    background: group.accent.bg,
                  }}
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="inline-flex items-center gap-2 min-w-0">
                      <GroupIcon size={16} style={{ color: group.accent.accent }} />
                      <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{group.label}</h3>
                    </div>
                    <span className="text-[11px] font-mono uppercase tracking-[0.14em]" style={{ color: 'var(--text-muted)' }}>
                      {group.toolCount}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    {group.topThree.map((tool, index) => (
                      <Link
                        key={`quick-${group.id}-${tool.slug || tool.id || tool.name}`}
                        to={tool.slug ? `/tools/${tool.slug}` : '/best'}
                        className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 transition-all hover:-translate-y-0.5"
                        style={{
                          background: isLight ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.06)',
                          border: isLight ? '1px solid rgba(15, 23, 42, 0.08)' : '1px solid rgba(255,255,255,0.10)',
                        }}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-mono uppercase tracking-[0.16em]" style={{ color: group.accent.accent }}>#{index + 1}</p>
                          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{tool.name}</p>
                        </div>
                        <ArrowRight size={14} style={{ color: group.accent.accent }} />
                      </Link>
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => handleStateChange('overall', group.id)}
                      className="text-xs px-3 py-1.5 rounded-full"
                      style={{
                        color: group.accent.accent,
                        border: `1px solid ${group.accent.accent}55`,
                        background: `${group.accent.accent}1A`,
                      }}
                    >
                      Open shortlist
                    </button>
                    {group.categorySlug && (
                      <Link
                        to={`/categories/${group.categorySlug}`}
                        className="text-xs"
                        style={{ color: 'var(--text-secondary)' }}
                      >
                        Full category
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 py-10 lg:py-14">
        <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-6 lg:gap-8">
          <aside className="terminal-grid p-4 lg:p-5 h-fit xl:sticky xl:top-28">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-cyber-yellow mb-1">Use Cases</p>
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Browse by category
                </h2>
              </div>
              <Award size={18} className="text-cyber-yellow" />
            </div>
            <div className="space-y-2 max-h-[68vh] overflow-y-auto pr-1">
              {useCaseGroups.map((group) => {
                const active = group.id === selectedGroup?.id;
                const GroupIcon = group.icon;
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => handleStateChange(activeCriterion, group.id)}
                    className="w-full text-left rounded-2xl px-4 py-3 transition-all"
                    style={{
                      border: `1px solid ${active ? `${group.accent.accent}55` : 'rgba(255,255,255,0.08)'}`,
                      background: active ? group.accent.bg : 'rgba(255,255,255,0.02)',
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: `${group.accent.accent}22`, color: group.accent.accent }}
                      >
                        <GroupIcon size={18} />
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-white truncate">{group.label}</div>
                        <div className="text-xs text-text-muted mt-1">{group.toolCount} ranked tools</div>
                        {group.useCases?.length > 0 && (
                          <div className="text-[11px] text-text-secondary mt-1 line-clamp-2">
                            {group.useCases.join(' - ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>

          <div className="space-y-6">
            <div className="terminal-grid p-5 lg:p-6 overflow-hidden">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-6">
                <div className="max-w-3xl">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-black/25 mb-4">
                    <SelectedGroupIcon size={14} style={{ color: selectedGroup?.accent?.accent || 'var(--glow-1)' }} />
                    <span className="text-xs uppercase tracking-[0.18em] font-mono" style={{ color: selectedGroup?.accent?.accent || 'var(--glow-1)' }}>
                      {selectedGroup?.label || 'Rankings'}
                    </span>
                  </div>
                  <h2 className="text-3xl text-white font-black mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    {selectedGroup?.label || 'Use-case rankings'}
                  </h2>
                  <p className="text-text-secondary max-w-2xl">{selectedGroup?.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-3 min-w-[240px]">
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-text-muted mb-2">Guides</div>
                    <div className="text-2xl font-black text-white">{selectedGroup?.pages?.length || 0}</div>
                  </div>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-text-muted mb-2">Shortlisted</div>
                    <div className="text-2xl font-black text-white">{selectedGroup?.toolCount || 0}</div>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-6">
                {SORT_OPTIONS.map((option) => {
                  const active = option.id === activeCriterion;
                  const OptionIcon = option.icon;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => handleStateChange(option.id, selectedGroup?.id)}
                      className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-mono uppercase tracking-widest transition-all"
                      style={{
                        background: active ? option.background : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${active ? option.border : 'rgba(255,255,255,0.08)'}`,
                        color: active ? option.accent : 'var(--text-secondary)',
                      }}
                    >
                      <OptionIcon size={14} /> {option.label}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {selectedShortlist.map((tool, index) => (
                  <Link
                    key={`${selectedSortOption.id}-${tool.slug || tool.id || tool.name}`}
                    to={tool.slug ? `/tools/${tool.slug}` : '/best'}
                    className="rounded-2xl p-5 block transition-transform hover:-translate-y-0.5"
                    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${selectedSortOption.border}` }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-[0.18em]" style={{ color: selectedSortOption.accent, background: `${selectedSortOption.accent}1a` }}>
                        #{index + 1}
                      </span>
                      {tool.rating?.overall ? (
                        <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: selectedSortOption.accent }}>
                          <Star size={13} className="fill-current" /> {tool.rating.overall}
                        </span>
                      ) : (
                        <span className="text-xs text-text-muted">No rating yet</span>
                      )}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{tool.name}</h3>
                    <p className="text-sm text-text-secondary mb-3 line-clamp-2">{tool.tagline || tool.best_for || 'Ranked for this use case.'}</p>
                    <div className="space-y-2 text-xs text-text-muted">
                      <div className="flex items-center justify-between gap-3">
                        <span>Price</span>
                        <span className="text-white text-right">{tool.price_label || 'Pricing varies'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span>Buyer signal</span>
                        <span className="text-white text-right">{tool.clicks || 0} clicks</span>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-sm mt-4" style={{ color: selectedSortOption.accent }}>
                      View tool <ArrowRight size={14} />
                    </span>
                  </Link>
                ))}
              </div>

              {!selectedShortlist.length && (
                <div className="rounded-2xl p-5 text-sm text-text-secondary" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  No shortlist data is available for this category yet.
                </div>
              )}

              {selectedGroup?.pages?.length > 0 && (
                <div className="mt-6 pt-6 border-t border-white/10">
                  <div className="flex items-center gap-2 mb-3 text-sm text-text-muted">
                    <Building2 size={15} /> Full editorial guides
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {selectedGroup.pages.slice(0, 5).map((page) => (
                      <Link
                        key={page.slug}
                        to={`/best/${page.slug}`}
                        className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm transition-colors"
                        style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'var(--text-primary)' }}
                      >
                        <span>{page.guide_badge || normalizeAudienceLabel(page.audience || page.use_case)}</span>
                        <ArrowRight size={13} />
                      </Link>
                    ))}
                    {selectedGroup.categorySlug && (
                      <Link
                        to={`/categories/${selectedGroup.categorySlug}`}
                        className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm"
                        style={{ background: 'rgba(var(--glow-1-rgb), 0.08)', border: '1px solid rgba(var(--glow-1-rgb), 0.22)', color: 'var(--glow-1)' }}
                      >
                        Browse full category <ArrowRight size={13} />
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>

            <section className="terminal-grid p-5 lg:p-6" data-testid="rankings-scan-matrix">
              <div className="flex items-center gap-3 mb-2">
                <BarChart3 size={20} className="text-laser-green" />
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  Scan the matrix
                </h2>
              </div>
              <p className="text-sm text-text-secondary mb-6 max-w-3xl">
                Every row is a use case. Every column is a ranking angle. Click any cell to jump straight into that shortlist without opening a separate landing page first.
              </p>

              {matrixRows.length === 0 && (
                <div className="flex items-center gap-2 py-3 text-text-muted text-xs">
                  <div className="animate-spin rounded-full h-3 w-3 border-b border-cyber-yellow" />
                  Building matrix...
                </div>
              )}

              <div className="hidden lg:block overflow-x-auto">
                <div className="grid gap-3 min-w-[1040px]" style={{ gridTemplateColumns: `240px repeat(${SORT_OPTIONS.length}, minmax(0, 1fr))` }}>
                  <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <div className="text-xs font-mono uppercase tracking-[0.18em] text-text-muted">Use Cases</div>
                    <div className="text-lg font-bold text-white mt-2">Category rail</div>
                  </div>
                  {SORT_OPTIONS.map((option) => {
                    const OptionIcon = option.icon;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleStateChange(option.id, selectedGroup?.id)}
                        className="rounded-2xl p-4 text-left"
                        style={{ background: option.background, border: `1px solid ${option.border}` }}
                      >
                        <div className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: option.accent }}>
                          <OptionIcon size={16} /> {option.label}
                        </div>
                      </button>
                    );
                  })}

                  {matrixRows.map((group) => {
                    const GroupIcon = group.icon;
                    return (
                      <React.Fragment key={group.id}>
                        <button
                          type="button"
                          onClick={() => handleStateChange(activeCriterion, group.id)}
                          className="rounded-2xl p-4 text-left transition-all"
                          style={{
                            background: group.id === selectedGroup?.id ? group.accent.bg : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${group.id === selectedGroup?.id ? `${group.accent.accent}44` : 'rgba(255,255,255,0.08)'}`,
                          }}
                        >
                          <div className="inline-flex items-center gap-2 mb-2" style={{ color: group.accent.accent }}>
                            <GroupIcon size={16} />
                            <span className="text-sm font-semibold text-white">{group.label}</span>
                          </div>
                          <div className="text-xs text-text-muted">{group.toolCount} ranked tools</div>
                        </button>
                        {group.cells.map((cell) => (
                          <button
                            key={`${group.id}-${cell.option.id}`}
                            type="button"
                            onClick={() => handleStateChange(cell.option.id, group.id)}
                            className="rounded-2xl p-4 text-left transition-transform hover:-translate-y-0.5"
                            style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${cell.option.border}` }}
                          >
                            {cell.tools.length > 0 ? (
                              <>
                                <div className="text-[11px] font-mono uppercase tracking-wide mb-1.5" style={{ color: cell.option.accent }}>{cell.option.shortLabel}</div>
                                <div className="text-sm font-semibold text-white line-clamp-2">{cell.tools[0].name}</div>
                                {cell.tools[1] && (
                                  <div className="text-xs text-text-muted mt-1 truncate">{cell.tools[1].name}</div>
                                )}
                              </>
                            ) : (
                              <div className="text-xs text-text-muted">No data</div>
                            )}
                          </button>
                        ))}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:hidden">
                {matrixRows.map((group) => {
                  const GroupIcon = group.icon;
                  return (
                    <div key={group.id} className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <button type="button" onClick={() => handleStateChange(activeCriterion, group.id)} className="flex items-center gap-2 mb-3 text-left">
                        <GroupIcon size={16} style={{ color: group.accent.accent }} />
                        <span className="text-base font-semibold text-white">{group.label}</span>
                      </button>
                      <div className="grid grid-cols-2 gap-2">
                        {group.cells.map((cell) => (
                          <button
                            key={`${group.id}-${cell.option.id}-mobile`}
                            type="button"
                            onClick={() => handleStateChange(cell.option.id, group.id)}
                            className="rounded-xl p-3 text-left"
                            style={{ background: cell.option.background, border: `1px solid ${cell.option.border}` }}
                          >
                            <div className="text-[11px] font-mono uppercase tracking-[0.14em] mb-2" style={{ color: cell.option.accent }}>
                              {cell.option.shortLabel}
                            </div>
                            <div className="text-sm text-white line-clamp-2">{cell.tools[0]?.name || 'No data yet'}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="terminal-grid p-5 lg:p-6">
                <div className="flex items-center gap-2 mb-3 text-cyber-yellow">
                  <TrendingUp size={18} />
                  <h2 className="text-xl font-bold text-white">Trending now</h2>
                </div>
                <div className="space-y-3">
                  {topTools.slice(0, 5).map((tool, index) => (
                    <Link key={tool.id || tool.slug} to={`/tools/${tool.slug}`} className="flex items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-white/5">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">#{index + 1} {tool.name}</div>
                        <div className="text-xs text-text-muted truncate">{tool.tagline || tool.category_name}</div>
                      </div>
                      <span className="text-xs font-mono text-cyber-yellow">{tool.clicks || 0} clicks</span>
                    </Link>
                  ))}
                </div>
              </div>

              <div className="terminal-grid p-5 lg:p-6">
                <div className="flex items-center gap-2 mb-3 text-cyber-yellow">
                  <Star size={18} />
                  <h2 className="text-xl font-bold text-white">Highest rated</h2>
                </div>
                <div className="space-y-3">
                  {topPicks.slice(0, 5).map((tool, index) => (
                    <Link key={tool.id || tool.slug} to={`/tools/${tool.slug}`} className="flex items-center justify-between rounded-xl px-4 py-3 transition-colors hover:bg-white/5">
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">#{index + 1} {tool.name}</div>
                        <div className="text-xs text-text-muted truncate">{tool.category_name || tool.tagline}</div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-mono text-cyber-yellow">
                        <Star size={12} className="fill-current" /> {tool.rating?.overall || '-'}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BestPage;

